import { useState } from "react";
import { Mail, Phone, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import emailjs from "@emailjs/browser";

export const Contact = () => {
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Please fill in all fields before sending.");
            return;
        }

        setLoading(true);

        emailjs.send(
            process.env.REACT_APP_EMAILJS_SERVICE_ID,
            process.env.REACT_APP_EMAILJS_TEMPLATE_ID, {
            name: form.name,
            email: form.email,
            message: form.message,
        }, process.env.REACT_APP_EMAILJS_PUBLIC_KEY)
            .then(() => {
                setLoading(false);
                setForm({ name: "", email: "", message: "" });
                toast.success("Message sent successfully 🚀");
            })
            .catch((error) => {
                setLoading(false);
                console.error(error);
                toast.error("Failed to send message. Try again.");
            });
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="py-24 lg:py-32 bg-white"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 lg:gap-24">
                <div>
                    <span className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-blue-700 mb-4 block font-display">
                        Get in touch
                    </span>
                    <h2
                        data-testid="contact-title"
                        className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]"
                    >
                        Let&apos;s build something that lasts.
                    </h2>
                    <p className="mt-6 text-base lg:text-lg leading-relaxed text-slate-600 max-w-md">
                        Tell us about your product, your team, or the problem
                        you&apos;re solving. We read every message.
                    </p>

                    <div className="mt-12 space-y-6 border-t border-slate-200 pt-10">
                        <a
                            href="mailto:contact@nextgensystems.in"
                            data-testid="contact-email-link"
                            className="group flex items-start gap-4"
                        >
                            <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-700 group-hover:bg-blue-700 group-hover:text-white transition-colors">
                                <Mail size={16} />
                            </span>
                            <div>
                                <div className="text-xs uppercase tracking-[0.18em] text-slate-400 font-medium">
                                    Email
                                </div>
                                <div className="mt-1 font-display text-xl sm:text-2xl font-medium text-slate-900 group-hover:text-blue-700 transition-colors">
                                    contact@nextgensystems.in
                                </div>
                            </div>
                        </a>

                        <div
                            data-testid="contact-phone"
                            className="flex items-start gap-4"
                        >
                            <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-700">
                                <Phone size={16} />
                            </span>
                            <div>
                                <div className="text-xs uppercase tracking-[0.18em] text-slate-400 font-medium">
                                    Phone
                                </div>
                                <div className="mt-1 font-display text-base lg:text-lg font-medium text-slate-900">
                                    Available on request
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <form
                    onSubmit={handleSubmit}
                    data-testid="contact-form"
                    className="bg-slate-50 border border-slate-200 rounded-2xl p-8 lg:p-10"
                >
                    <div className="space-y-5">
                        <div>
                            <label
                                htmlFor="name"
                                className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2"
                            >
                                Name
                            </label>
                            <input
                                id="name"
                                name="name"
                                type="text"
                                value={form.name}
                                onChange={handleChange}
                                data-testid="contact-input-name"
                                placeholder="Jane Doe"
                                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-slate-400 text-slate-900"
                            />
                        </div>
                        <div>
                            <label
                                htmlFor="email"
                                className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2"
                            >
                                Email
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                value={form.email}
                                onChange={handleChange}
                                data-testid="contact-input-email"
                                placeholder="jane@company.com"
                                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-slate-400 text-slate-900"
                            />
                        </div>
                        <div>
                            <label
                                htmlFor="message"
                                className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2"
                            >
                                Message
                            </label>
                            <textarea
                                id="message"
                                name="message"
                                rows={5}
                                value={form.message}
                                onChange={handleChange}
                                data-testid="contact-input-message"
                                placeholder="Tell us what you're working on..."
                                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-slate-400 text-slate-900 resize-none"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="contact-submit-button"
                            className="group w-full inline-flex items-center justify-center px-8 py-4 bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 text-white font-medium rounded-full transition-colors duration-200"
                        >
                            {loading ? "Sending..." : "Send Message"}
                            {!loading && (
                                <ArrowRight
                                    size={16}
                                    className="ml-2 transition-transform group-hover:translate-x-1"
                                />
                            )}
                        </button>
                        <p className="text-xs text-slate-400 text-center">
                            Your message will be delivered securely. We respect your privacy and will never share your information without consent.
                        </p>
                    </div>
                </form>
            </div>
        </section>
    );
};
